import { ART_PAL, body, fade, mix, n, rng, shade, star, tint, vgrad, warm, type ArtTheme } from '../covers/kit';
import { defineScene, type SceneVariant } from './types';

/**
 * Red Envelopes scene behind the game zone: a festive hall — a faint gold diamond lattice at both edges fading out
 * towards the middle, a warm light pool over the round table and, on the wide scene only, the festive things in the
 * free bands beside the table: red lanterns on cords that fade out upwards (no cord runs from the top edge under the
 * title row), tangerines with leaves and a closed red envelope (no characters on it) low at both sides, a few gold
 * sparkles. Nothing runs under the panels (title row, stake tabs, the status line, the right rail). The phone has no
 * free band beside the table, so its scene is the lattice, the light and a few sparkles. The centre stays calm.
 * No filters, no text; no groups of four (Chinese taboo, docs/art-prompts.md).
 * Motifs are marked `data-motif="<x>"` (tests keep them out of the middle 40%).
 */
export function hongbaoScene(theme: ArtTheme, v: SceneVariant): string {
  const P = ART_PAL[theme];
  const p = `hb${theme[0]}${v[0]}`;
  const wide = v === 'wide';
  const W = wide ? 1600 : 400;
  const H = wide ? 900 : 800;

  const top = P.dark ? mix(P.bg, P.red, 0.16) : mix(P.gold, '#FFFFFF', 0.84);
  const mid = P.dark ? mix(P.bg, P.red, 0.1) : mix(P.red, '#FFFFFF', 0.91);
  const bottom = P.dark ? mix(P.bg, P.orange, 0.08) : mix(P.orange, '#FFFFFF', 0.88);
  const gold = warm(P);
  const red = P.dark ? mix(P.red, P.bg, 0.12) : P.red;
  const orange = P.dark ? mix(P.orange, P.bg, 0.1) : tint(P.orange, 0.1);
  const leaf = P.dark ? mix(P.green, P.bg, 0.45) : shade(P.green, 0.1);
  const o = (dark: number, light: number) => n(P.dark ? dark : light);

  // lattice: a diamond tile (a lucky knot net), faded towards the middle by a mask
  const T = wide ? 36 : 26;
  const h = T / 2;
  const tile =
    `<pattern id="${p}lt" width="${T}" height="${T}" patternUnits="userSpaceOnUse">` +
    `<path d="M${n(h)} 1L${n(T - 1)} ${n(h)}L${n(h)} ${n(T - 1)}L1 ${n(h)}Z" fill="none" stroke="${gold}" stroke-width="${wide ? 1.3 : 1}"/>` +
    `<circle cx="${n(h)}" cy="${n(h)}" r="${n(T * 0.06)}" fill="${gold}"/></pattern>`;
  const band = wide ? 320 : 60;
  let defs =
    `${vgrad(`${p}bg`, top, mid, bottom)}${tile}` +
    `<linearGradient id="${p}fl" x1="0" x2="1"><stop offset="0" stop-color="#FFF" stop-opacity=".9"/><stop offset="1" stop-color="#FFF" stop-opacity="0"/></linearGradient>` +
    `<linearGradient id="${p}fr" x1="1" x2="0"><stop offset="0" stop-color="#FFF" stop-opacity=".9"/><stop offset="1" stop-color="#FFF" stop-opacity="0"/></linearGradient>` +
    `<mask id="${p}ml"><rect width="${band}" height="${H}" fill="url(#${p}fl)"/></mask>` +
    `<mask id="${p}mr"><rect x="${W - band}" width="${band}" height="${H}" fill="url(#${p}fr)"/></mask>` +
    fade(`${p}pool`, gold, P.dark ? 0.13 : 0.2) +
    fade(`${p}calm`, P.dark ? P.bg : '#FFFFFF', P.dark ? 0.32 : 0.42);
  if (wide)
    defs +=
      body(`${p}lan`, red, P.dark ? 0.28 : 0.35, 0.3, 0.9, 0.9) +
      `<radialGradient id="${p}tan" cx=".36" cy=".32" r=".75"><stop offset="0" stop-color="${tint(orange, 0.35)}"/><stop offset=".55" stop-color="${orange}"/><stop offset="1" stop-color="${shade(orange, 0.28)}"/></radialGradient>` +
      `<linearGradient id="${p}cd" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${gold}" stop-opacity="0"/><stop offset="1" stop-color="${gold}" stop-opacity=".8"/></linearGradient>` +
      fade(`${p}glow`, P.dark ? P.orange : P.gold, P.dark ? 0.22 : 0.18);

  /** a round red lantern at (x, y) — a cord fading upwards, gold caps, ribs, a gold tassel */
  const lantern = (x: number, y: number, s: number) => {
    const X = (d: number) => n(x + d * s);
    const Y = (d: number) => n(y + d * s);
    const rib = (rx: number) =>
      `<ellipse cx="${n(x)}" cy="${n(y)}" rx="${n(rx * s)}" ry="${n(34 * s)}" fill="none" stroke="${shade(red, 0.3)}" stroke-opacity=".55" stroke-width="${n(1.6 * s)}"/>`;
    let tassel = '';
    for (let i = -2; i <= 2; i++)
      tassel += `<path d="M${X(i * 2)} ${Y(44)}L${X(i * 3.5)} ${Y(76)}" stroke="${gold}" stroke-width="${n(1.4 * s)}"/>`;
    return (
      `<g data-motif="${Math.round(x)}">` +
      `<ellipse cx="${n(x)}" cy="${n(y)}" rx="${n(70 * s)}" ry="${n(62 * s)}" fill="url(#${p}glow)"/>` +
      `<rect x="${X(-0.8)}" y="${Y(-150)}" width="${n(1.6 * s)}" height="${n(118 * s)}" fill="url(#${p}cd)"/>` +
      `<ellipse cx="${n(x)}" cy="${n(y)}" rx="${n(42 * s)}" ry="${n(35 * s)}" fill="url(#${p}lan)"/>` +
      rib(24) +
      rib(9) +
      `<rect x="${X(-17)}" y="${Y(-40)}" width="${n(34 * s)}" height="${n(9 * s)}" rx="${n(2.5 * s)}" fill="${gold}"/>` +
      `<rect x="${X(-17)}" y="${Y(31)}" width="${n(34 * s)}" height="${n(9 * s)}" rx="${n(2.5 * s)}" fill="${gold}"/>` +
      `<g fill="none" stroke-linecap="round">${tassel}</g>` +
      `<ellipse cx="${X(-18)}" cy="${Y(-14)}" rx="${n(9 * s)}" ry="${n(15 * s)}" transform="rotate(25 ${X(-18)} ${Y(-14)})" fill="#FFFFFF" fill-opacity="${o(0.14, 0.28)}"/>` +
      `</g>`
    );
  };

  /** a tangerine with a leaf pair */
  const tangerine = (x: number, y: number, r: number, flip: 1 | -1) =>
    `<circle cx="${n(x)}" cy="${n(y)}" r="${n(r)}" fill="url(#${p}tan)"/>` +
    `<path d="M${n(x)} ${n(y - r * 0.9)}q${n(flip * r * 0.5)} ${n(-r * 0.7)} ${n(flip * r * 1.1)} ${n(-r * 0.35)}q${n(-flip * r * 0.55)} ${n(r * 0.5)} ${n(-flip * r * 1.1)} ${n(r * 0.35)}Z" fill="${leaf}"/>` +
    `<circle cx="${n(x)}" cy="${n(y - r * 0.9)}" r="${n(r * 0.1)}" fill="${shade(leaf, 0.3)}"/>`;

  /** a closed red envelope, tilted, gold flap line and a round gold clasp (no characters) */
  const envelope = (x: number, y: number, rot: number) =>
    `<g transform="rotate(${rot} ${n(x)} ${n(y)})">` +
    `<rect x="${n(x - 26)}" y="${n(y - 36)}" width="52" height="72" rx="5" fill="${red}"/>` +
    `<path d="M${n(x - 26)} ${n(y - 22)}Q${n(x)} ${n(y - 2)} ${n(x + 26)} ${n(y - 22)}" fill="none" stroke="${gold}" stroke-width="2"/>` +
    `<circle cx="${n(x)}" cy="${n(y - 9)}" r="7" fill="${gold}"/></g>`;

  /** a table-top group: tangerines and an envelope (left group of three, right of two) */
  const group = (x: number, y: number, flip: 1 | -1, three: boolean) => {
    const f = (d: number) => x + d * flip;
    return (
      `<g data-motif="${Math.round(x)}">` +
      `<ellipse cx="${n(f(20))}" cy="${n(y + 34)}" rx="120" ry="14" fill="${P.dark ? '#000' : shade(P.ink, 0.2)}" fill-opacity="${o(0.3, 0.08)}"/>` +
      envelope(f(64), y - 2, flip * 14) +
      tangerine(f(-40), y + 10, 26, flip) +
      tangerine(f(8), y + 16, 22, flip) +
      (three ? tangerine(f(-16), y - 22, 20, flip) : '') +
      `</g>`
    );
  };

  /** gold sparkles scattered in the side bands only */
  const sparkles = (count: number, seed: number, rMax: number) => {
    const r = rng(seed);
    let s = '';
    for (let i = 0; i < count; i++) {
      const side = i % 2 ? 1 : 0;
      const x = side ? W - band * 0.95 * r() : band * 0.95 * r();
      const y = H * (0.24 + r() * 0.64);
      const R = rMax * (0.45 + r() * 0.55);
      s += `<polygon points="${star(x, y, R, R * 0.28, 4)}"/>`;
    }
    return `<g fill="${gold}" fill-opacity="${o(0.45, 0.6)}">${s}</g>`;
  };

  let art =
    `<rect width="${W}" height="${H}" fill="url(#${p}bg)"/>` +
    `<g opacity="${o(0.1, 0.14)}"><rect width="${band}" height="${H}" fill="url(#${p}lt)" mask="url(#${p}ml)"/>` +
    `<rect x="${W - band}" width="${band}" height="${H}" fill="url(#${p}lt)" mask="url(#${p}mr)"/></g>` +
    `<ellipse cx="${W / 2}" cy="${n(H * 0.1)}" rx="${n(W * 0.36)}" ry="${n(H * 0.5)}" fill="url(#${p}pool)"/>` +
    `<ellipse cx="${W / 2}" cy="${n(H * 0.52)}" rx="${n(W * 0.32)}" ry="${n(H * 0.4)}" fill="url(#${p}calm)"/>` +
    sparkles(wide ? 9 : 5, wide ? 5 : 3, wide ? 9 : 6);

  // the wide scene is cropped to the game zone (cover): at 1440 × 900 about x 230…1415 of it is seen, the round table
  // with its seats covers x ≈ 500…1100, the title row and the stake tabs sit over the top left (y < 110), the status
  // line and the disclaimer over the bottom left (y > 830)
  if (wide)
    art +=
      lantern(300, 320, 1) +
      lantern(425, 470, 0.72) +
      lantern(1290, 250, 1.05) +
      lantern(1170, 410, 0.72) +
      lantern(1352, 470, 0.62) +
      group(345, 710, 1, true) +
      group(1270, 730, -1, false);

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid slice">` +
    `<defs>${defs}</defs>` +
    art +
    `</svg>`
  );
}

/** Header accent of Red Envelopes: a gold scallop (auspicious cloud wave) on a thin line (repeat-x tile, 16 × 8). */
export function hongbaoAccent(theme: ArtTheme): string {
  const P = ART_PAL[theme];
  const gold = warm(P);
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 8" width="16" height="8">` +
    `<path d="M0 7.5H16M0 7Q4 1.5 8 7T16 7" fill="none" stroke="${gold}" stroke-opacity="${P.dark ? 0.7 : 0.85}"/>` +
    `<circle cx="8" cy="3" r="1" fill="${P.red}"/>` +
    `</svg>`
  );
}

export default defineScene({ game: 'hongbao', scene: hongbaoScene, accent: hongbaoAccent });
